import { useState, useEffect } from 'react';
import { useAuth, canSeeCost, canSeeMargin, canSeeSellPrice, canEditMappings, canRunSync, canExport } from '../context/AuthContext';

const S = {
  page: { padding: '32px 36px', fontFamily: "'Inter', sans-serif" },
  topbar: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 600, color: 'var(--text1)', margin: 0 },
  sub: { fontSize: 14, color: 'var(--text3)', marginTop: 4 },
  btnRow: { display: 'flex', gap: 8 },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 13, color: 'var(--text2)', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 },
  stat: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px' },
  statLabel: { fontSize: 12, color: 'var(--text3)', margin: '0 0 6px', fontWeight: 500 },
  statVal: { fontSize: 26, fontWeight: 600, color: 'var(--text1)', margin: 0 },
  tabs: { display: 'flex', gap: 0, marginBottom: 16, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', width: 'fit-content' },
  tab: { padding: '8px 18px', fontSize: 13, cursor: 'pointer', background: 'var(--bg3)', border: 'none', fontFamily: "'Inter', sans-serif", color: 'var(--text2)', fontWeight: 500 },
  tabActive: { background: 'var(--purple)', color: '#fff' },
  tableWrap: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' },
  td: { padding: '13px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text2)', verticalAlign: 'middle' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
  productLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 12 },
  input: { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 13, color: 'var(--text2)', fontFamily: "'Inter', sans-serif" },
};

function StatusBadge({ mapped }) {
  return mapped
    ? <span style={{ ...S.badge, background: 'var(--gbg)', color: 'var(--green)' }}>Mapped</span>
    : <span style={{ ...S.badge, background: 'var(--obg)', color: 'var(--orange)' }}>Unmapped</span>;
}

function MarginBar({ pct, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 80, height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: Math.min(pct, 100) + '%', height: '100%', background: color || 'var(--green)', borderRadius: 99 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: color || 'var(--green)' }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

export default function CustomersPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('profitability');
  const [customers, setCustomers] = useState([]);
  const [qbCustomers, setQbCustomers] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState({ abn: '', email: '', notes: '', is_azure: false, azure_margin_pct: 15 });
  const [branchesCustomer, setBranchesCustomer] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchForm, setBranchForm] = useState({ qb_customer_id: '', qb_customer_name: '', product_id: '', product_name: '', quantity: 1, sell_price_override: '' });
  const [branchSaving, setBranchSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [branchEdits, setBranchEdits] = useState({});
  const [branchEditSaving, setBranchEditSaving] = useState(false);
  const [branchEditError, setBranchEditError] = useState('');

  // Branch modal permissions: everyone can edit quantity;
  // price overrides and branch deletion are Admin + Accountant only.
  const userRole = user?.role || 'staff';
  const canEditBranchPrice = ['admin', 'accountant'].includes(userRole);
  const canDeleteBranch = ['admin', 'accountant'].includes(userRole);

  useEffect(() => {
    Promise.all([
      fetch('/api/customers').then(r => r.json()),
      fetch('/api/qb-customers').then(r => r.json()),
    ]).then(([c, qb]) => {
      setCustomers(Array.isArray(c) ? c : []);
      setQbCustomers(Array.isArray(qb) ? qb : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = customers.filter(c => {
    const matchSearch = c.lc_account_name && c.lc_account_name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'mapped' ? c.qb_customer_id : !c.qb_customer_id);
    return matchSearch && matchFilter;
  });

  const totalRevenue = customers.reduce((s, c) => s + (c.monthly_revenue || 0), 0);
  const totalCost = customers.reduce((s, c) => s + (c.monthly_cost || 0), 0);
  const totalProfit = totalRevenue - totalCost;
  const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0;

  const handleSync = async () => {
    setSyncing(true);
    await fetch('/api/sync/import-accounts', { method: 'POST' });
    const c = await fetch('/api/customers').then(r => r.json());
    setCustomers(Array.isArray(c) ? c : []);
    setSyncing(false);
  };

  const saveDetails = async () => {
    await fetch('/api/customers/' + editingCustomer.lc_account_id, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm),
    });
    setCustomers(prev => prev.map(c => c.lc_account_id === editingCustomer.lc_account_id ? { ...c, ...editForm } : c));
    setEditingCustomer(null);
  };

  const handleMapSave = async (lcId, qbId) => {
    await fetch('/api/customers/map', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lc_account_id: lcId, qb_customer_id: qbId }),
    });
    const c = await fetch('/api/customers').then(r => r.json());
    setCustomers(Array.isArray(c) ? c : []);
  };

  const openBranches = async (customer) => {
    setBranchesCustomer(customer);
    const res = await fetch('/api/customers/' + customer.lc_account_id + '/branches');
    const data = await res.json();
    setBranches(Array.isArray(data) ? data : []);
    setBranchForm({ qb_customer_id: '', qb_customer_name: '', product_id: '', product_name: '', quantity: 1, sell_price_override: '' });
    setProductSearch('');
    setProductResults([]);
    setBranchEdits({});
    setBranchEditError('');
  };

  const saveBranchEdits = async () => {
    setBranchEditSaving(true);
    setBranchEditError('');
    try {
      for (const [id, edit] of Object.entries(branchEdits)) {
        const res = await fetch('/api/branch-mappings/' + id, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(edit),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.error || 'Save failed');
        }
      }
      const res = await fetch('/api/customers/' + branchesCustomer.lc_account_id + '/branches');
      const data = await res.json();
      setBranches(Array.isArray(data) ? data : []);
      setBranchEdits({});
    } catch (e) {
      setBranchEditError(e.message);
    }
    setBranchEditSaving(false);
  };

  const searchProducts = async (q) => {
    setProductSearch(q);
    setBranchForm(f => ({ ...f, product_name: q, product_id: '' }));
    if (q.length < 2) { setProductResults([]); return; }
    const res = await fetch('/api/products/search?q=' + encodeURIComponent(q));
    const data = await res.json();
    setProductResults(Array.isArray(data) ? data : []);
  };

  const selectProduct = (product) => {
    setProductSearch(product.name);
    setBranchForm(f => ({ ...f, product_id: product.id, product_name: product.name }));
    setProductResults([]);
  };

  const addBranch = async () => {
    if (!branchForm.qb_customer_id || !branchForm.qb_customer_name) return;
    setBranchSaving(true);
    await fetch('/api/customers/' + branchesCustomer.lc_account_id + '/branches', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(branchForm),
    });
    const res = await fetch('/api/customers/' + branchesCustomer.lc_account_id + '/branches');
    setBranches(await res.json());
    setBranchForm({ qb_customer_id: '', qb_customer_name: '', product_id: '', product_name: '', quantity: 1, sell_price_override: '' });
    setProductSearch('');
    setProductResults([]);
    setBranchSaving(false);
    setCustomers(prev => prev.map(c => c.lc_account_id === branchesCustomer.lc_account_id ? { ...c, has_branches: true } : c));
  };

  const deleteBranch = async (id) => {
    if (!window.confirm('Remove this branch? Its separate invoice will no longer be generated. To change license counts, edit the quantity instead.')) return;
    await fetch('/api/customers/' + branchesCustomer.lc_account_id + '/branches/' + id, { method: 'DELETE' });
    const res = await fetch('/api/customers/' + branchesCustomer.lc_account_id + '/branches');
    const updated = await res.json();
    setBranches(updated);
    if (updated.length === 0) setCustomers(prev => prev.map(c => c.lc_account_id === branchesCustomer.lc_account_id ? { ...c, has_branches: false } : c));
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text3)' }}>Loading customers...</div>;

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div>
          <h1 style={S.title}>Customers</h1>
          <p style={S.sub}>Mappings, billing, and profitability per client</p>
        </div>
        <div style={S.btnRow}>
          {canRunSync(user) && (
            <button style={S.btn} onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Sync accounts'}
            </button>
          )}
        </div>
      </div>

      <div style={S.stats}>
        <div style={S.stat}><p style={S.statLabel}>Total accounts</p><p style={S.statVal}>{customers.length}</p></div>
        <div style={S.stat}><p style={S.statLabel}>Mapped to QB</p><p style={{ ...S.statVal, color: 'var(--green)' }}>{customers.filter(c => c.qb_customer_id).length}</p></div>
        <div style={S.stat}>
          <p style={S.statLabel}>Unmapped</p>
          <p style={{ ...S.statVal, color: customers.filter(c => !c.qb_customer_id).length > 0 ? 'var(--orange)' : 'var(--text1)' }}>
            {customers.filter(c => !c.qb_customer_id).length}
          </p>
        </div>
        {canSeeMargin(user) ? (
          <div style={S.stat}><p style={S.statLabel}>Avg margin</p><p style={{ ...S.statVal, color: avgMargin > 15 ? 'var(--green)' : 'var(--orange)' }}>{avgMargin.toFixed(1)}%</p></div>
        ) : (
          <div style={S.stat}><p style={S.statLabel}>Last synced</p><p style={{ ...S.statVal, fontSize: 16, paddingTop: 6 }}>Just now</p></div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={S.tabs}>
          {canSeeMargin(user) && (
            <button style={{ ...S.tab, ...(tab === 'profitability' ? S.tabActive : {}) }} onClick={() => setTab('profitability')}>Profitability</button>
          )}
          <button style={{ ...S.tab, ...(tab === 'mappings' ? S.tabActive : {}) }} onClick={() => setTab('mappings')}>Mappings</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...S.input, width: 200 }} placeholder="Search accounts..." value={search} onChange={e => setSearch(e.target.value)} />
          <select style={{ ...S.input, width: 140 }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="mapped">Mapped</option>
            <option value="unmapped">Unmapped</option>
          </select>
        </div>
      </div>

      {tab === 'profitability' && canSeeMargin(user) && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 18 }}>
            {[
              { label: 'Monthly revenue', val: '$' + totalRevenue.toLocaleString(), color: 'var(--text1)' },
              { label: 'Monthly cost', val: '$' + totalCost.toLocaleString(), color: 'var(--text1)' },
              { label: 'Total profit', val: '$' + totalProfit.toLocaleString(), color: 'var(--green)' },
              { label: 'Avg margin', val: avgMargin.toFixed(1) + '%', color: avgMargin > 15 ? 'var(--green)' : 'var(--orange)' },
            ].map(s => (
              <div key={s.label} style={{ ...S.stat, borderTop: '3px solid ' + s.color }}>
                <p style={S.statLabel}>{s.label}</p>
                <p style={{ ...S.statVal, color: s.color, fontSize: 22 }}>{s.val}</p>
              </div>
            ))}
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Customer</th>
                  {canSeeCost(user) && <th style={{ ...S.th, textAlign: 'right' }}>Cost/mo</th>}
                  <th style={{ ...S.th, textAlign: 'right' }}>Revenue/mo</th>
                  {canSeeMargin(user) && <th style={{ ...S.th, textAlign: 'right' }}>Profit</th>}
                  {canSeeMargin(user) && <th style={S.th}>Margin</th>}
                  <th style={S.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const profit = (c.monthly_revenue || 0) - (c.monthly_cost || 0);
                  const margin = c.monthly_revenue > 0 ? (profit / c.monthly_revenue * 100) : 0;
                  const isExpanded = expanded === c.lc_account_id;
                  return [
                    <tr key={c.lc_account_id} onClick={() => setExpanded(isExpanded ? null : c.lc_account_id)} style={{ cursor: 'pointer' }}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{c.lc_account_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{c.product_count || 0} products ID {c.lc_account_id}</div>
                      </td>
                      {canSeeCost(user) && <td style={{ ...S.td, textAlign: 'right', fontWeight: 500 }}>{'$' + (c.monthly_cost || 0).toLocaleString()}</td>}
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 500 }}>{'$' + (c.monthly_revenue || 0).toLocaleString()}</td>
                      {canSeeMargin(user) && (
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: profit >= 0 ? 'var(--green)' : 'var(--red)' }}>
                          {(profit >= 0 ? '+' : '') + '$' + profit.toLocaleString()}
                        </td>
                      )}
                      {canSeeMargin(user) && (
                        <td style={S.td}>
                          <MarginBar pct={margin} color={margin >= 20 ? 'var(--green)' : margin >= 10 ? 'var(--orange)' : 'var(--red)'} />
                        </td>
                      )}
                      <td style={S.td}><StatusBadge mapped={!!c.qb_customer_id} /></td>
                    </tr>,
                    isExpanded && c.products && c.products.length > 0 && (
                      <tr key={c.lc_account_id + '-expand'}>
                        <td colSpan={6} style={{ ...S.td, background: 'var(--bg3)', padding: '12px 20px' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 8 }}>Product breakdown</div>
                          {c.products.map((p, i) => {
                            const pProfit = (p.sell_price - p.cost_price) * (p.quantity || 1);
                            const pMargin = p.sell_price > 0 ? ((p.sell_price - p.cost_price) / p.sell_price * 100) : 0;
                            return (
                              <div key={i} style={{ ...S.productLine, ...(i === c.products.length - 1 ? { borderBottom: 'none' } : {}) }}>
                                <span style={{ color: 'var(--text2)' }}>{p.name} x {p.quantity || 1}</span>
                                <span style={{ color: 'var(--text3)' }}>
                                  {canSeeCost(user) && 'Cost $' + (p.cost_price * (p.quantity || 1)).toFixed(2) + ' '}
                                  {canSeeSellPrice(user) && 'Sell $' + (p.sell_price * (p.quantity || 1)).toFixed(2) + ' '}
                                  {canSeeMargin(user) && <span style={{ color: pProfit >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{'+$' + pProfit.toFixed(2) + ' (' + pMargin.toFixed(1) + '%)'}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                    )
                  ];
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: 'var(--text3)', padding: 32 }}>No customers found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {(tab === 'mappings' || !canSeeMargin(user)) && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>LeaderCloud account</th>
                <th style={S.th}>QuickBooks customer</th>
                <th style={S.th}>Status</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Last invoice</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <CustomerMappingRow
                  key={c.lc_account_id}
                  customer={c}
                  qbCustomers={qbCustomers}
                  canEdit={canEditMappings(user)}
                  onEdit={c => { setEditingCustomer(c); setEditForm({ abn: c.abn || '', email: c.email || '', notes: c.notes || '', is_azure: c.is_azure || false, azure_margin_pct: c.azure_margin_pct || 15 }); }}
                  onBranches={openBranches}
                  canSync={canRunSync(user)}
                  onSave={handleMapSave}
                />
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ ...S.td, textAlign: 'center', color: 'var(--text3)', padding: 32 }}>No customers found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>
        Showing {filtered.length} of {customers.length} accounts
      </p>

      {branchesCustomer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--card)', borderRadius: 12, padding: 28, width: 620, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text1)' }}>{branchesCustomer.lc_account_name} - Branch Billing</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Each branch gets its own QB invoice</div>
              </div>
              <button onClick={() => setBranchesCustomer(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>x</button>
            </div>

            {branches.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                  Configured Branches ({branches.length})
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 70px 95px 30px', background: 'var(--bg3)', padding: '8px 12px', fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase' }}>
                    <div>QB Customer</div><div>Product</div><div>Qty</div><div>Price</div><div></div>
                  </div>
                  {branches.map(b => {
                    const edit = branchEdits[b.id] || {};
                    const qtyVal = edit.quantity !== undefined ? edit.quantity : b.quantity;
                    const priceVal = edit.sell_price_override !== undefined ? edit.sell_price_override : (b.sell_price_override == null ? '' : b.sell_price_override);
                    const isDirty = edit.quantity !== undefined || edit.sell_price_override !== undefined;
                    return (
                      <div key={b.id} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 70px 95px 30px', padding: '10px 12px', borderTop: '1px solid var(--border)', alignItems: 'center', fontSize: 12, background: isDirty ? 'var(--tint)' : 'transparent' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{b.qb_customer_name}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)' }}>ID: {b.qb_customer_id}</div>
                        </div>
                        <div style={{ color: 'var(--text2)', fontSize: 11, paddingRight: 8 }}>{b.product_name || 'All products'}</div>
                        <div style={{ paddingRight: 8 }}>
                          <input type="number" min="1" step="1"
                            style={{ width: '100%', padding: '5px 7px', borderRadius: 6, border: '1px solid ' + (edit.quantity !== undefined ? 'var(--purple-bright)' : 'var(--border)'), fontSize: 12, boxSizing: 'border-box', textAlign: 'center' }}
                            value={qtyVal}
                            onChange={e => {
                              const v = parseInt(e.target.value);
                              setBranchEdits(prev => {
                                const next = { ...prev, [b.id]: { ...prev[b.id], quantity: isNaN(v) ? '' : v } };
                                if (!isNaN(v) && v === b.quantity) { delete next[b.id].quantity; if (Object.keys(next[b.id]).length === 0) delete next[b.id]; }
                                return next;
                              });
                            }} />
                        </div>
                        <div style={{ paddingRight: 8 }}>
                          {canEditBranchPrice ? (
                            <input type="number" step="0.01" min="0" placeholder="Default"
                              style={{ width: '100%', padding: '5px 7px', borderRadius: 6, border: '1px solid ' + (edit.sell_price_override !== undefined ? 'var(--purple-bright)' : 'var(--border)'), fontSize: 12, boxSizing: 'border-box', textAlign: 'right' }}
                              value={priceVal}
                              onChange={e => {
                                const raw = e.target.value;
                                setBranchEdits(prev => {
                                  const next = { ...prev, [b.id]: { ...prev[b.id], sell_price_override: raw } };
                                  const orig = b.sell_price_override == null ? '' : String(b.sell_price_override);
                                  if (raw === orig) { delete next[b.id].sell_price_override; if (Object.keys(next[b.id]).length === 0) delete next[b.id]; }
                                  return next;
                                });
                              }} />
                          ) : (
                            <span style={{ color: 'var(--text2)' }} title="Price overrides: Admin or Accountant only">{b.sell_price_override ? '$' + b.sell_price_override : 'Default'}</span>
                          )}
                        </div>
                        {canDeleteBranch ? (
                          <button onClick={() => deleteBranch(b.id)} title="Remove branch" style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, padding: 0 }}>x</button>
                        ) : <div />}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                    Total seats across branches: <b style={{ color: 'var(--text1)' }}>{branches.reduce((s, b) => { const q = branchEdits[b.id]?.quantity; const v = q !== undefined && q !== '' ? q : b.quantity; return s + (parseInt(v) || 0); }, 0)}</b>
                    <span style={{ color: 'var(--text3)' }}> · edits apply to future syncs only · all changes are logged</span>
                  </div>
                  {Object.keys(branchEdits).length > 0 && (
                    <button onClick={saveBranchEdits} disabled={branchEditSaving}
                      style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: 'var(--purple)', color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                      {branchEditSaving ? 'Saving...' : 'Save changes (' + Object.keys(branchEdits).length + ')'}
                    </button>
                  )}
                </div>
                {branchEditError && (
                  <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 8, background: 'var(--rbg)', borderRadius: 6, padding: '8px 12px' }}>
                    {branchEditError}
                  </div>
                )}
              </div>
            )}

            {branches.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 12, padding: '14px 0 20px' }}>
                No branches configured for this customer yet.{!canDeleteBranch ? ' An Admin or Accountant can set up branches here.' : ' Add the first branch below.'}
              </div>
            )}

            {canDeleteBranch ? (
            <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Add Branch</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>QB Customer ID</label>
                  <input style={{ width: '100%', marginTop: 3, padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, boxSizing: 'border-box' }}
                    placeholder="e.g. 1185"
                    value={branchForm.qb_customer_id}
                    onChange={e => setBranchForm(f => ({ ...f, qb_customer_id: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>QB Customer Name</label>
                  <input style={{ width: '100%', marginTop: 3, padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, boxSizing: 'border-box' }}
                    placeholder="e.g. Birches ELC Bunyip"
                    value={branchForm.qb_customer_name}
                    onChange={e => setBranchForm(f => ({ ...f, qb_customer_name: e.target.value }))} />
                </div>
                <div style={{ position: 'relative', gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>Product (type to search)</label>
                  <input
                    style={{ width: '100%', marginTop: 3, padding: '7px 10px', borderRadius: 6, border: '1px solid ' + (branchForm.product_id ? 'var(--purple-bright)' : 'var(--border)'), fontSize: 12, boxSizing: 'border-box' }}
                    placeholder="Type product name to search..."
                    value={productSearch}
                    onChange={e => searchProducts(e.target.value)}
                    autoComplete="off"
                  />
                  {branchForm.product_id && (
                    <div style={{ fontSize: 10, color: 'var(--purple-bright)', marginTop: 2 }}>Selected product linked</div>
                  )}
                  {productResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 200, maxHeight: 220, overflowY: 'auto' }}>
                      {productResults.map(p => (
                        <div
                          key={p.id}
                          onClick={() => selectProduct(p)}
                          style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12 }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--tint)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'var(--card)'}
                        >
                          <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{p.name}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                            {p.default_sell_price ? 'Sell: $' + parseFloat(p.default_sell_price).toFixed(2) : 'No price set'}
                            {p.ms_rrp ? ' | RRP: $' + parseFloat(p.ms_rrp).toFixed(2) : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>Quantity</label>
                  <input type="number" min="1"
                    style={{ width: '100%', marginTop: 3, padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, boxSizing: 'border-box' }}
                    value={branchForm.quantity}
                    onChange={e => setBranchForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>Price Override (optional)</label>
                  <input type="number" step="0.01"
                    style={{ width: '100%', marginTop: 3, padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, boxSizing: 'border-box' }}
                    placeholder="Leave blank to use default"
                    value={branchForm.sell_price_override}
                    onChange={e => setBranchForm(f => ({ ...f, sell_price_override: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setBranchesCustomer(null)}
                  style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 12, cursor: 'pointer' }}>
                  Close
                </button>
                <button onClick={addBranch} disabled={branchSaving || !branchForm.qb_customer_id || !branchForm.qb_customer_name}
                  style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'var(--purple)', color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600, opacity: (!branchForm.qb_customer_id || !branchForm.qb_customer_name) ? 0.5 : 1 }}>
                  {branchSaving ? 'Adding...' : 'Add Branch'}
                </button>
              </div>
            </div>
            ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>You can edit branch quantities. Adding or removing branches and price overrides require Admin or Accountant.</span>
              <button onClick={() => setBranchesCustomer(null)}
                style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 12, cursor: 'pointer', color: 'var(--text2)', flexShrink: 0 }}>
                Close
              </button>
            </div>
            )}
          </div>
        </div>
      )}

      {editingCustomer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--card)', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text1)' }}>{editingCustomer.lc_account_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Customer Details</div>
              </div>
              <button onClick={() => setEditingCustomer(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>x</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>ABN</label>
                <input style={{ width: '100%', marginTop: 4, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  placeholder="e.g. 86 491 990 075"
                  value={editForm.abn || ''}
                  onChange={e => setEditForm(f => ({ ...f, abn: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Billing Email</label>
                <input style={{ width: '100%', marginTop: 4, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  placeholder="e.g. accounts@customer.com.au"
                  value={editForm.email || ''}
                  onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Notes</label>
                <textarea style={{ width: '100%', marginTop: 4, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', minHeight: 70, boxSizing: 'border-box' }}
                  placeholder="Internal notes..."
                  value={editForm.notes || ''}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
                <div style={{ fontSize: 11, color: 'var(--purple)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Azure Billing</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <label style={{ fontSize: 13, color: 'var(--text2)', flex: 1 }}>Azure customer</label>
                  <div
                    onClick={() => setEditForm(f => ({ ...f, is_azure: !f.is_azure }))}
                    style={{ width: 44, height: 24, borderRadius: 12, background: editForm.is_azure ? 'var(--purple)' : 'var(--border)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: editForm.is_azure ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: 'var(--card)', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </div>
                {editForm.is_azure && (
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Azure Margin %</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <input type="number" min="0" max="100" step="0.5"
                        style={{ width: 100, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--purple)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                        value={editForm.azure_margin_pct || 15}
                        onChange={e => setEditForm(f => ({ ...f, azure_margin_pct: parseFloat(e.target.value) || 15 }))} />
                      <span style={{ fontSize: 13, color: 'var(--text3)' }}>% margin on Azure usage</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                      e.g. 15% on $474.36 cost = $545.51 charged to customer
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setEditingCustomer(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={saveDetails} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--purple)', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>Save Details</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerMappingRow({ customer: c, qbCustomers, canEdit, canSync, onSave, onEdit, onBranches }) {
  const [selectedQb, setSelectedQb] = useState(c.qb_customer_id || '');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(c.lc_account_id, selectedQb);
    setSaving(false);
  };

  const handleSync = async (e) => {
    e.stopPropagation();
    setSyncing(true);
    await fetch('/api/sync/run', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ account_id: c.lc_account_id }) });
    setSyncing(false);
  };

  const S2 = {
    td: { padding: '13px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text2)', verticalAlign: 'middle' },
    btn: { padding: '5px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  };

  return (
    <tr>
      <td style={S2.td}>
        <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{c.lc_account_name}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>ID: {c.lc_account_id}</div>
      </td>
      <td style={S2.td}>
        {canEdit ? (
          <select style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, width: '100%' }}
            value={selectedQb} onChange={e => setSelectedQb(e.target.value)}>
            <option value="">Select QB customer</option>
            {qbCustomers.map(q => (
              <option key={q.Id} value={q.Id}>{q.DisplayName}</option>
            ))}
          </select>
        ) : (
          <span style={{ color: c.qb_customer_id ? 'var(--text2)' : 'var(--text3)' }}>
            {c.qb_customer_name || qbCustomers.find(q => String(q.Id) === String(c.qb_customer_id))?.DisplayName || '-'}
          </span>
        )}
      </td>
      <td style={S2.td}><StatusBadge mapped={!!c.qb_customer_id} /></td>
      <td style={{ ...S2.td, textAlign: 'right', color: 'var(--text3)' }}>
        {c.last_invoice_date ? new Date(c.last_invoice_date).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : '-'}
      </td>
      <td style={{ ...S2.td, textAlign: 'right' }}>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          {canEdit && selectedQb !== c.qb_customer_id && (
            <button style={S2.btn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          )}
          {c.qb_customer_id && canSync && (
            <button style={{ ...S2.btn, background: 'var(--purple)', color: '#fff', border: '1px solid var(--purple)' }} onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing' : 'Sync now'}
            </button>
          )}
          {canEdit && onEdit && (
            <button style={S2.btn} onClick={() => onEdit(c)}>Details</button>
          )}
          {onBranches && (
            <button
              style={{ ...S2.btn, background: c.has_branches ? 'var(--tint)' : 'var(--border)', border: c.has_branches ? '1px solid var(--purple-bright)' : '1px solid var(--border)', color: c.has_branches ? 'var(--purple-bright)' : 'var(--text2)' }}
              onClick={() => onBranches(c)}>
              Branches
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
